import React from 'react';
import { motion } from 'framer-motion';
import { Phone, MessageCircle, Download } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/data/translations';
import LanguageLogo from '@/components/Logo';
import { LANDING } from '@/constants/testIds';

export default function StickyFooter({ onOpenBrochure }) {
  const { lang } = useLanguage();
  const t = translations[lang].footer;

  return (
    <>
      {/* Spacer for sticky footer */}
      <div className="h-20 sm:h-16" />

      {/* Info Footer (scrollable) */}
      <footer className="bg-[#1A2520] text-white/50 py-8 pb-24 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <LanguageLogo lang={lang} variant="dark" size="sm" showTagline={false} />
            <p className="text-xs text-center sm:text-right max-w-md">{t.disclaimer}</p>
          </div>
          <div className="mt-4 pt-4 border-t border-white/10 text-center">
            <p className="text-[11px]">{t.copyright}</p>
            <p data-testid="footer-powered-by" className="text-[10px] mt-1 text-white/30">{t.poweredBy}</p>
          </div>
        </div>
      </footer>

      {/* Floating Brochure CTA */}
      <motion.button
        onClick={onOpenBrochure}
        data-testid={LANDING.floatingCta}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 2, duration: 0.4, type: 'spring' }}
        className="fixed bottom-20 sm:bottom-[72px] right-4 z-50 flex items-center gap-2 bg-[#D48C57] hover:bg-[#C07A4A] text-white px-4 py-3 rounded-full shadow-lg shadow-[#D48C57]/30 transition-all duration-200 hover:-translate-y-0.5 text-sm font-medium"
      >
        <Download className="w-4 h-4" />
        <span className="hidden sm:inline">{t.brochure}</span>
      </motion.button>

      {/* Sticky Bottom Bar */}
      <div
        data-testid={LANDING.stickyFooter}
        className="fixed bottom-0 left-0 right-0 z-50 bg-[#FDFBF7]/95 backdrop-blur-xl border-t border-[#E5DFD3] shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2.5 sm:py-3">
          <div className="flex items-center gap-2 sm:gap-3">
            {/* WhatsApp CTA */}
            <a
              href={`https://wa.me/919403890991?text=${encodeURIComponent('Hi, I am interested in Lakshmi Nivasa plots at Red Hills. Please share details.')}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={LANDING.ctaWhatsapp}
              className="flex-1 flex items-center justify-center gap-1.5 bg-[#25D366] hover:bg-[#20BD5B] text-white py-3 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 shadow-md shadow-[#25D366]/20 min-w-0"
            >
              <MessageCircle className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{t.whatsapp}</span>
            </a>

            {/* Phone CTA */}
            <a
              href="tel:+919403890991"
              data-testid={LANDING.ctaPhone}
              className="flex-1 flex items-center justify-center gap-1.5 bg-[#2C4C3B] hover:bg-[#213A2D] text-white py-3 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 shadow-md min-w-0"
            >
              <Phone className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{t.call}</span>
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
